import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { webPageSchema } from "@/components/schema";

const PATH = "/corrections";
const TITLE = "Corrections";
const DESCRIPTION =
  "A public log of factual corrections made to pieces published on Spain Smile Guide.";

interface CorrectionEntry {
  date: string;
  articleTitle: string;
  articlePath: string;
  correction: string;
  why: string;
}

const ENTRIES: CorrectionEntry[] = [
  {
    date: "2026-04-18",
    articleTitle: "Initial publication",
    articlePath: "/",
    correction:
      "Initial publication. No corrections to report yet. This page will be updated as the publication matures — every factual correction to a published piece appears here, dated, with the article, the change, and the reason.",
    why:
      "The corrections log exists on day one rather than on first-error because the presence of the page is itself the commitment. Readers should be able to find the corrections policy before there is anything to record.",
  },
];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function Corrections() {
  return (
    <>
      <SEO
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        type="website"
        jsonLd={webPageSchema(TITLE, DESCRIPTION, PATH)}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Corrections", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Publication"
        title="Corrections"
        standfirst="A public, chronological record of every factual correction made to a piece on Spain Smile Guide. Most-recent first. Silent edits are not used."
        datePublished="2026-04-18"
        dateModified="2026-04-18"
      />

      <div className="container-prose prose-editorial mt-10">
        <h2>How this page works</h2>
        <p>
          When a published piece on this site contains a factual error — a
          misquoted credential, an outdated website link, a misattributed
          statement, an incorrect date, a wrong location — the piece is
          corrected in place and the correction is logged here. Each entry
          records the date, the article, a plain description of what
          changed, and the reason the original was wrong. Substantive
          corrections are also noted in a short prepended note at the top of
          the affected article for one month after the correction appears.
        </p>
        <p>
          Typographical fixes and formatting repairs are not logged here.
          Anything that changes the factual content of a piece is.
        </p>

        <h2>Log</h2>
        <ol className="!list-none !pl-0">
          {ENTRIES.map((entry, i) => (
            <li
              key={i}
              className="border-t border-rule pt-6 !mt-0 [&:not(:first-child)]:mt-10"
            >
              <div className="eyebrow mb-2">
                <time dateTime={entry.date}>{formatDate(entry.date)}</time>
              </div>
              <h3 className="font-serif text-h3 text-ink">
                {entry.articlePath === "/" ? (
                  entry.articleTitle
                ) : (
                  <Link to={entry.articlePath} className="no-underline hover:underline">
                    {entry.articleTitle}
                  </Link>
                )}
              </h3>
              <p className="mt-3 text-ink">
                <strong className="font-sans text-xs uppercase tracking-wider">
                  Correction —
                </strong>{" "}
                {entry.correction}
              </p>
              <p className="mt-3 text-small text-ink/80">
                <strong className="font-sans text-xs uppercase tracking-wider">
                  Why —
                </strong>{" "}
                {entry.why}
              </p>
            </li>
          ))}
        </ol>

        <h2>How to report an error</h2>
        <p>
          If you find a factual error in anything we have published, write
          to the editorial desk via the address on our{" "}
          <Link to="/contact">contact page</Link>. Correction requests with
          a clear factual citation are prioritised. We do not acknowledge
          disagreements of interpretation as corrections — those are
          handled in the text of future pieces or by revision to the
          rubric on our{" "}
          <Link to="/methodology">methodology page</Link>, and the
          revision is itself logged.
        </p>

        <hr className="rule" />
        <p className="text-small text-charcoal/70">
          This page is updated as corrections are made. It is never
          reordered. It is never edited to remove an entry.
        </p>
      </div>
    </>
  );
}
